import React, { useEffect, useState } from "react";
import { Input, List, Select } from "antd";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { DeleteOutlined, DragOutlined, PlusOutlined ,QuestionCircleOutlined} from "@ant-design/icons";

const DynamicPicklistComponent = ({ value = [], onChange, isSelect, selectOptions, selectedFields }) => {
  const [options, setOptions] = useState(value); // Maintain picklist options locally
  const [editingIndex, setEditingIndex] = useState(null); // Track which option is being edited
  const [editingValue, setEditingValue] = useState(""); // Store the edited value for the current editing option
  const [inputValue, setInputValue] = useState(""); // For the new input value to be added


  const handleAddOption = () => {
    if (!inputValue.trim()) return; // Avoid adding empty values
    // Split the input value by commas and trim each value
    const newOptions = inputValue.split(",").map((val) => val.trim()).filter(Boolean);

    // Merge the new options with the existing ones, ensuring no duplicates
    const updatedOptions = [...new Set([...options, ...newOptions])];
    setOptions(updatedOptions);
    if (!isSelect) {
      setInputValue("");
    } // Reset input value after adding
    onChange?.(updatedOptions); // Notify parent about changes
  };

  useEffect(() => {
    if (selectedFields?.length > 0) {
      setOptions(selectedFields);
    }else{
      setOptions([]);
    }
  }, [selectedFields]);

  const handleDeleteOption = (index) => {
    const updatedOptions = [...options];
    updatedOptions.splice(index, 1);
    setOptions(updatedOptions);
    onChange?.(updatedOptions);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedOptions = Array.from(options);
    const [movedOption] = reorderedOptions.splice(result.source.index, 1);
    reorderedOptions.splice(result.destination.index, 0, movedOption);

    setOptions(reorderedOptions);
    onChange?.(reorderedOptions);
  };

  const handleSelect = (value) => {
    setInputValue(value);
  };

  useEffect(() => {
    if (isSelect && inputValue !== "") {
      handleAddOption();
    }
  }, [inputValue]);

  const handleEditOption = (index, newValue) => {
    const updatedOptions = [...options];
    updatedOptions[index] = newValue;
    setOptions(updatedOptions);
    onChange?.(updatedOptions);
    setEditingValue(""); // Clear editing value once the edit is complete
    setEditingIndex(null); // Exit editing mode
  };

  const handleChangeEditingValue = (e) => {
    setEditingValue(e.target.value);
  };

  return (
    <div>
      {!isSelect && (
        <Input
          placeholder="Enter picklist value"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onPressEnter={handleAddOption}
          style={{ marginBottom: "8px" }}
          addonAfter={
            <PlusOutlined
              onClick={handleAddOption}
              style={{ cursor: "pointer", color: "#1890ff" }}
            />
          }
        />
      )} 
      {isSelect && (
        <Select
        showSearch
        placeholder="Select fields to display"
        onChange={(e) => handleSelect(e)}
        style={{ width: '100%' }}
        defaultValue={options}
        value={inputValue}
        options={[
          ...selectOptions,
          { name: 'created_at', label: 'Created At' },
          { name: 'updated_at', label: 'Updated At' }
        ]
          .filter((field) => !options.includes(field.name))
          .map((field) => ({ value: field.name, label: field.label }))
        }
      />
      
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="options-list">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ maxHeight: "200px", overflowY: "auto" }}
            >
              {options.length > 0 && (
                <List bordered>
                  {options.map((option, index) => (
                    <Draggable key={option} draggableId={option} index={index}>
                      {(provided) => (
                        <List.Item
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            ...provided.draggableProps.style,
                          }}
                        >
                          <DragOutlined
                            style={{
                              cursor: "move",
                              fontSize: "16px",
                            }}
                          />
                          {isSelect ? (
                            <span>{option}</span> // Non-editable if isSelect is true
                          ) : editingIndex === index ? (
                            <Input
                              value={editingValue || options[index]} // Use editingValue for live changes
                              onChange={handleChangeEditingValue}
                              onPressEnter={() => handleEditOption(index, editingValue)}
                              onBlur={() => handleEditOption(index, editingValue)} // Blur to save the value
                            />
                          ) : (
                            <span
                              onClick={() => {
                                setEditingIndex(index); // Enable editing mode on click
                                setEditingValue(options[index]); // Set current value in editing state
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              {option}
                            </span>
                          )}
                          <DeleteOutlined
                            onClick={() => handleDeleteOption(index)}
                            style={{ color: "red", cursor: "pointer" }}
                          />
                        </List.Item>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default DynamicPicklistComponent;